import React, { Key, useEffect, useState } from 'react'
import Flickity from 'react-flickity-component'
import { registerComponent } from 'react-register-dom'

import TeaserSliderFeaturedArticle from './TeaserSliderFeaturedArticle'
import TeaserSliderMainArticle from './TeaserSliderMainArticle'
import { HeaderTag, TeaserSliderItem, TeaserSliderProperties } from './TeaserSliderTypes'

const flickityOptions = {
  cellAlign: 'left',
  pageDots: false,
  draggable: false,
  contain: true,
}

const TeaserSlider: React.FC<{ jsonData: string }> = ({ jsonData }) => {
  let parsedData: TeaserSliderProperties | null = null

  try {
    parsedData = JSON.parse(jsonData)

    if (!parsedData?.headline || !parsedData?.headlineHeadingLevel || !parsedData?.blogArticles) {
      console.error('TeaserSlider: Missing required data fields in JSON input')
      return null
    }
  } catch (error) {
    console.error('TeaserSlider: Invalid JSON input', error)
    return null
  }

  const teaserSliderItems = parsedData as TeaserSliderProperties
  const HeaderTag = (teaserSliderItems.headlineHeadingLevel = 'h2' as HeaderTag)

  const filteredTeaserSliderItems = teaserSliderItems.blogArticles.filter(
    (item: TeaserSliderItem) => item.image && item.title,
  )

  const ariaLabels = {
    tagLabel: teaserSliderItems?.tagLabel || 'Categories: ',
    dateLabel: teaserSliderItems?.dateLabel || 'Date: ',
    articleDescriptionLabel: teaserSliderItems?.articleDescriptionLabel || 'Description: ',
  }

  const flickityReference = React.useRef<Flickity | null>(null)
  const articlesReference = React.useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    // NOTE: empty dependencies array guarantees this will be there
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    flickityReference!.current!.on('change', setSelectedIndex)
    return () => {
      flickityReference.current?.off('change', setSelectedIndex)
    }
  }, [])
  const handleFeaturedArticleClick = (selectedIndex: number) => {
    flickityReference.current?.select(selectedIndex)
  }
  return (
    <div className={`cmp-teaser-slider`}>
      <HeaderTag className="cmp-teaser-slider__headline">{teaserSliderItems.headline}</HeaderTag>
      <div className="cmp-teaser-slider__main-article">
        <Flickity
          flickityRef={(reference: any) => {
            flickityReference.current = reference
          }}
          className={'cmp-teaser-slider__slider'} // default ''
          elementType="div" // default 'div'
          options={flickityOptions} // takes flickity options {}
          disableImagesLoaded={false} // default false
        >
          {filteredTeaserSliderItems.map((item: TeaserSliderItem, index: Key) => (
            <TeaserSliderMainArticle
              key={index}
              image={item.image}
              formattedPublicationDate={item.formattedPublicationDate}
              blogTags={item.blogTags}
              buttonLabel={item.buttonLabel}
              title={item.title}
              description={item.description}
              pageLink={{
                externalizedURL: item.pageLink?.externalizedURL,
                isTargetBlank: item.pageLink?.isTargetBlank,
              }}
              ariaLabels={ariaLabels}
            />
          ))}
        </Flickity>
      </div>
      <div className="cmp-teaser-slider__featured-articles" ref={articlesReference}>
        {filteredTeaserSliderItems.length > 1 &&
          filteredTeaserSliderItems.map((item: TeaserSliderItem, index: number) => (
            <TeaserSliderFeaturedArticle
              key={`${item.id}_${index}`}
              itemIndex={index} // for aria-labelledby
              item={item}
              isSelected={index === selectedIndex}
              onClick={() => handleFeaturedArticleClick(index)}
              containerElement={articlesReference.current}
              ariaLabels={ariaLabels}
            />
          ))}
      </div>
    </div>
  )
}

registerComponent('TeaserSlider', TeaserSlider)
export default TeaserSlider
